import type { TabItem } from '@/app/components/Tabs';
import type { SelectorOption } from '@/app/components/form/Selector/types';
import { REMINDER_DAYS, type ReminderDayType, type TaskStatus } from '@/types/clientTasksApiTypes';
import type { Nullable } from '@/types/common';
import type { CardType } from '../types';
import AmericanExpressIcon from '/public/icons/cards/american-express.svg';
import DiscoverIcon from '/public/icons/cards/discover.svg';
import MastercardIcon from '/public/icons/cards/mastercard.svg';
import VisaIcon from '/public/icons/cards/visa.svg';
import NotesIcon from '/public/icons/notes.svg';
import RelatedTravelersIcon from '/public/icons/passengers.svg';
import MainIcon from '/public/icons/profile-picture.svg';
import TasksIcon from '/public/icons/task-completed.svg';
import UnknownCardIcon from '/public/icons/unknown-card.svg';

export const TAB_QUERY_KEY = 'tab';
export const CARD_ID_QUERY_KEY = 'card_id';
export const CARD_LOCAL_STORAGE_KEY = 'cc-tokens';
export const CARD_PENDING_VERIFICATION_LOCAL_STORAGE_KEY = 'cc-pending-verifications';

export const PENDING_VERIFICATION_TTL_MS = 2 * 60 * 1000;
export const CLIENT_QUERY_KEY = 'client';
export const CLIENT_SETTINGS_QUERY_KEY = 'client_settings';
export const SHARE_SIGNATURE_QUERY_KEY = 'share_signature';
export const USER_EXPORT_SETTINGS_QUERY_KEY = 'user_export_settings';
export const CREDIT_CARDS_LIST_QUERY_KEY = 'credit_cards_list';
export const CARD_DETAILS_QUERY_KEY = 'card_details';
export const PREFERRED_CREDIT_CARDS_QUERY_KEY = 'preferred_credit_cards';
export const AIR_REQUESTS_QUERY_KEY = 'air_requests';
export const CARD_TYPE_QUERY_KEY = 'card_type';
export const CARD_LAST_4_QUERY_KEY = 'card_last_4';
export const CARD_ACTION_QUERY_KEY = 'card_action';
export const REQUEST_CARD_DETAILS_MODAL_OPEN = 'is_rc_open';

// Notes
export const CLIENT_NOTES_QUERY_KEY = 'client-notes';
export const NOTES_PAGE_QUERY_KEY = 'notes_page';
export const NOTES_PER_PAGE_QUERY_KEY = 'notes_per_page';

// Tasks
export const CLIENT_TASKS_QUERY_KEY = 'client-tasks';
export const TASKS_PAGE_QUERY_KEY = 'tasks_page';
export const TASKS_PER_PAGE_QUERY_KEY = 'tasks_per_page';
export const TASKS_STATUS_QUERY_KEY = 'tasks_status';
export const TASKS_SORT_QUERY_KEY = 'tasks_sort';
export const TASKS_SORT_DIR_QUERY_KEY = 'tasks_sort_dir';

export const TASK_STATUS_BG: Record<TaskStatus, string> = {
  OPEN: '#F1C40F33',
  COMPLETED: '#E1F0E8',
  CLOSED: '#F8F9FA',
};

export const TASK_STATUS_CONFIG: Record<TaskStatus, { bg: string }> = {
  OPEN: { bg: TASK_STATUS_BG.OPEN },
  COMPLETED: { bg: TASK_STATUS_BG.COMPLETED },
  CLOSED: { bg: TASK_STATUS_BG.CLOSED },
};

export const TASK_STATUS_LABEL: Record<TaskStatus, string> = {
  OPEN: 'Open',
  COMPLETED: 'Completed',
  CLOSED: 'Closed',
};

export const TASK_STATUS_TEXT_COLOR = '#062060';

export const TASK_STATUS_OPTIONS: SelectorOption<TaskStatus>[] = [
  { value: 'OPEN', label: TASK_STATUS_LABEL.OPEN },
  { value: 'COMPLETED', label: TASK_STATUS_LABEL.COMPLETED },
  { value: 'CLOSED', label: TASK_STATUS_LABEL.CLOSED },
];

export const DEFAULT_TASK_STATUS: TaskStatus = 'OPEN';

export const TASK_REMINDER_OPTIONS: SelectorOption<Nullable<ReminderDayType>>[] = [
  { value: null, label: 'No reminder' },
  { value: REMINDER_DAYS.ONE_DAY, label: '1 day before deadline' },
  { value: REMINDER_DAYS.THREE_DAYS, label: '3 days before deadline' },
  { value: REMINDER_DAYS.TEN_DAYS, label: '10 days before deadline' },
];

export const TASK_REMINDER_VIEW_LABELS: Record<ReminderDayType, string> = {
  [REMINDER_DAYS.ONE_DAY]: 'One day before due date',
  [REMINDER_DAYS.THREE_DAYS]: 'Three days before due date',
  [REMINDER_DAYS.TEN_DAYS]: 'Ten days before due date',
};

export const TASK_DETAILS_MAX_LENGTH = 300;
export const REQUEST_CARD_DETAILS_REDIRECT = 'is_rc_redirect';
export const SHARE_CARD_DETAILS_MODAL_OPEN = 'is_scd_open';
export const SHARE_CARD_DETAILS_FORM_TYPE_QUERY_KEY = 'scd_form_type';
export const CLIENT_DETAILS_QUERY_KEY = 'client-details';
export const DRAFT_CLIENT_DETAILS_QUERY_KEY = 'draft-client-details';
export const CLIENT_PREFERENCES_QUERY_KEY = 'client-preferences';
export const SHARE_CARD_DETAILS_FORM_TYPE = {
  PROFILE_FORM: 'profile-form',
  CREDIT_CARD_FORM: 'credit-card-form',
} as const;

export type ShareCardDetailsFormType = (typeof SHARE_CARD_DETAILS_FORM_TYPE)[keyof typeof SHARE_CARD_DETAILS_FORM_TYPE];

export const MAX_CREDIT_CARDS_COUNT = 7;
export const MAX_NICKNAME_LENGTH = 17;

export const CardAction = {
  CREATE: 'create',
  EDIT: 'edit',
} as const;

export const clientTabs = {
  main: 'main',
  relatedTravelers: 'related-travelers',
  notes: 'notes',
  tasks: 'tasks',
} as const;

type ClientDetailTabKey = keyof typeof clientTabs;

export const clientTabsLabels: Record<ClientDetailTabKey, string> = {
  main: 'Primary profile',
  relatedTravelers: 'Related passengers',
  notes: 'Notes',
  tasks: 'Tasks',
};

export const CardDetailsRequestType = {
  REQUEST: 'request',
  VERIFICATION: 'verification',
  ACCESS_GRANTED: 'access-granted',
} as const;

export const CardDetailsRequestMethod = {
  EMAIL: 'email',
  SMS: 'sms',
} as const;

export const CardTypes = {
  Visa: 'Visa',
  MasterCard: 'MasterCard',
  AmericanExpress: 'AmericanExpress',
  Discover: 'Discover',
} as const;

export const CardIcons: Record<CardType, React.FC<React.SVGProps<SVGSVGElement>>> = {
  Visa: VisaIcon,
  MasterCard: MastercardIcon,
  AmericanExpress: AmericanExpressIcon,
  Discover: DiscoverIcon,
  unknown: UnknownCardIcon,
};

export const CardLabels: Record<CardType, string> = {
  Visa: 'Visa',
  MasterCard: 'Mastercard',
  AmericanExpress: 'American Express',
  Discover: 'Discover',
  unknown: 'New credit card',
};

// Sabre GDS FOP (Form of Payment) two-letter codes
export const SabreFOPCodes: Record<CardType, string> = {
  Visa: 'VI',
  MasterCard: 'CA',
  AmericanExpress: 'AX',
  Discover: 'DS',
  unknown: 'XX',
};

export const crmTabs: Record<(typeof clientTabs)[keyof typeof clientTabs], string> = {
  main: 'pills-main-tab',
  'related-travelers': 'pills-related-traveler-tab',
  tasks: 'pills-task-tab',
  notes: 'pills-note-tab',
};

export const clientTabsOptions: TabItem[] = [
  { id: clientTabs.main, label: clientTabsLabels.main, Icon: MainIcon },
  { id: clientTabs.relatedTravelers, label: clientTabsLabels.relatedTravelers, Icon: RelatedTravelersIcon },
  { id: clientTabs.notes, label: clientTabsLabels.notes, Icon: NotesIcon },
  { id: clientTabs.tasks, label: clientTabsLabels.tasks, Icon: TasksIcon },
];
